import React, {useEffect, useRef, useState, useCallback} from "react";
import {useLoadScript} from "@react-google-maps/api";

type Shape = {
    id: string;
    type: "circle" | "polygon" | "line";
    color: string;
} & (
    | {type: "circle"; center: [number, number]; radiusKm: number}
    | {type: "polygon" | "line"; vertices: Array<[number, number]>}
);

type DrawingTool = "circle" | "polygon" | "line" | "none";

const EARTH_RADIUS_KM = 6371;

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (deg: number) => deg * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return EARTH_RADIUS_KM * c;
}

// Vertex shader - simple pass-through for full-screen quad
const vertexShaderSource = `
    attribute vec2 a_position;
    varying vec2 v_texCoord;

    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        // Convert from clip space (-1 to 1) to texture coords (0 to 1)
        // Note: Y is flipped here so (0,0) = top-left, (1,1) = bottom-right
        v_texCoord = vec2(a_position.x * 0.5 + 0.5, -a_position.y * 0.5 + 0.5);
    }
`;

// Fragment shader for rendering circles using Web Mercator projection
const circleFragmentShaderSource = `
    precision highp float;

    varying vec2 v_texCoord;

    uniform vec2 u_center; // lat, lng in degrees
    uniform float u_radiusKm;
    uniform vec4 u_color;
    uniform vec2 u_resolution;
    uniform float u_zoom; // Google Maps zoom level
    uniform vec4 u_bounds; // minLat, maxLat, minLng, maxLng

    const float EARTH_RADIUS_KM = 6371.0;
    const float PI = 3.14159265359;

    float toRad(float deg) {
        return deg * PI / 180.0;
    }

    // Haversine distance on sphere - geodesically accurate
    float haversineDistance(float lat1, float lng1, float lat2, float lng2) {
        float dLat = toRad(lat2 - lat1);
        float dLng = toRad(lng2 - lng1);
        float a = sin(dLat * 0.5) * sin(dLat * 0.5) +
                  cos(toRad(lat1)) * cos(toRad(lat2)) *
                  sin(dLng * 0.5) * sin(dLng * 0.5);
        float c = 2.0 * atan(sqrt(a), sqrt(1.0 - a));
        return EARTH_RADIUS_KM * c;
    }

    // Check if circle covers the pole by calculating if radius reaches beyond pole
    bool circleCoversNorthPole(float centerLat, float radiusKm) {
        // Calculate angular distance in degrees
        float angularDistanceDeg = (radiusKm / EARTH_RADIUS_KM) * (180.0 / PI);
        return centerLat + angularDistanceDeg >= 85.05112878;
    }

    bool circlesCoversSouthPole(float centerLat, float radiusKm) {
        float angularDistanceDeg = (radiusKm / EARTH_RADIUS_KM) * (180.0 / PI);
        return centerLat - angularDistanceDeg <= -85.05112878;
    }

    void main() {
        // Convert texture coord to lat/lng
        // v_texCoord now: (0,0) = top-left, (1,1) = bottom-right (flipped in vertex shader)
        // u_bounds: (minLat, maxLat, minLng, maxLng)
        // Map X from left (0) to right (1) => minLng to maxLng
        // Map Y from top (0) to bottom (1) => maxLat to minLat
        float lng = u_bounds.z + v_texCoord.x * (u_bounds.w - u_bounds.z);
        float lat = u_bounds.y - v_texCoord.y * (u_bounds.y - u_bounds.x);

        // DEBUG: Show where we think the center is (should be red at viewport center)
        // Comment this out after testing
        /*
        if (abs(v_texCoord.x - 0.5) < 0.01 && abs(v_texCoord.y - 0.5) < 0.01) {
            gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0); // Magenta crosshair at texture center
            return;
        }
        */

        // Store original lat before clamping (for pole detection)
        float originalLat = lat;

        // Clamp latitude to valid range
        lat = clamp(lat, -85.05112878, 85.05112878);

        // Check if we're at the pole edge and circle covers it
        bool atNorthEdge = originalLat >= 85.0;
        bool atSouthEdge = originalLat <= -85.0;
        bool coversNorthPole = circleCoversNorthPole(u_center.x, u_radiusKm);
        bool coversSouthPole = circlesCoversSouthPole(u_center.x, u_radiusKm);

        // If at pole edge and circle covers pole, fill it completely
        if ((atNorthEdge && coversNorthPole) || (atSouthEdge && coversSouthPole)) {
            gl_FragColor = vec4(u_color.rgb, u_color.a);
            return;
        }

        // Check for circle using geodesic (haversine) distance
        // Test current position and wrapped positions for dateline handling
        float minDistance = 1e10; // Very large number

        // Check at three longitude offsets: -360, 0, +360 (for dateline wrapping)
        for (float lngOffset = -360.0; lngOffset <= 360.0; lngOffset += 360.0) {
            float wrappedCenterLng = u_center.y + lngOffset;

            // Calculate geodesic distance using haversine
            float distance = haversineDistance(u_center.x, wrappedCenterLng, lat, lng);

            minDistance = min(minDistance, distance);
        }

        // Anti-aliased edge (smooth over ~5km for visual quality)
        float edgeDistance = 5.0;
        float alpha = smoothstep(u_radiusKm + edgeDistance, u_radiusKm - edgeDistance, minDistance);

        gl_FragColor = vec4(u_color.rgb, u_color.a * alpha);
    }
`;

// Fragment shader for compositing with blend modes
const compositeFragmentShaderSource = `
    precision highp float;

    varying vec2 v_texCoord;

    uniform sampler2D u_texture;
    uniform vec4 u_tintColor;

    void main() {
        vec4 texColor = texture2D(u_texture, v_texCoord);
        gl_FragColor = texColor * u_tintColor;
    }
`;

// WebGL Overlay factory function (called after Google Maps is loaded)
function createWebGLOverlay(drawFuncRef: React.MutableRefObject<(() => void) | null>) {
    class WebGLOverlay extends google.maps.OverlayView {
        private isReady = false;

        onAdd() {
            this.isReady = true;
        }

        draw() {
            if (drawFuncRef.current) {
                drawFuncRef.current();
            }
        }

        onRemove() {
            this.isReady = false;
        }

        getProjectionSafe() {
            return this.isReady ? this.getProjection() : null;
        }
    }

    return new WebGLOverlay();
}

class WebGLRenderer {
    gl: WebGLRenderingContext;
    circleProgram: WebGLProgram | null = null;
    compositeProgram: WebGLProgram | null = null;
    quadBuffer: WebGLBuffer | null = null;
    maskFramebuffer: WebGLFramebuffer | null = null;
    maskTexture: WebGLTexture | null = null;

    constructor(canvas: HTMLCanvasElement) {
        const gl = canvas.getContext("webgl", {
            alpha: true,
            premultipliedAlpha: false,
            antialias: false
        });

        if (!gl) {
            throw new Error("WebGL not supported");
        }

        this.gl = gl;
        this.initShaders();
        this.initBuffers();
        this.initFramebuffer(canvas.width, canvas.height);
    }

    compileShader(source: string, type: number): WebGLShader | null {
        const shader = this.gl.createShader(type);
        if (!shader) return null;

        this.gl.shaderSource(shader, source);
        this.gl.compileShader(shader);

        if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
            console.error("Shader compilation error:", this.gl.getShaderInfoLog(shader));
            this.gl.deleteShader(shader);
            return null;
        }

        return shader;
    }

    createProgram(vertexSource: string, fragmentSource: string): WebGLProgram | null {
        const vertexShader = this.compileShader(vertexSource, this.gl.VERTEX_SHADER);
        const fragmentShader = this.compileShader(fragmentSource, this.gl.FRAGMENT_SHADER);

        if (!vertexShader || !fragmentShader) return null;

        const program = this.gl.createProgram();
        if (!program) return null;

        this.gl.attachShader(program, vertexShader);
        this.gl.attachShader(program, fragmentShader);
        this.gl.linkProgram(program);

        if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
            console.error("Program linking error:", this.gl.getProgramInfoLog(program));
            this.gl.deleteProgram(program);
            return null;
        }

        return program;
    }

    initShaders() {
        this.circleProgram = this.createProgram(vertexShaderSource, circleFragmentShaderSource);
        this.compositeProgram = this.createProgram(vertexShaderSource, compositeFragmentShaderSource);
    }

    initBuffers() {
        // Full-screen quad
        const quad = new Float32Array([
            -1, -1,
             1, -1,
            -1,  1,
             1,  1
        ]);

        this.quadBuffer = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadBuffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, quad, this.gl.STATIC_DRAW);
    }

    initFramebuffer(width: number, height: number) {
        // Create texture for mask
        this.maskTexture = this.gl.createTexture();
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.maskTexture);
        this.gl.texImage2D(
            this.gl.TEXTURE_2D, 0, this.gl.RGBA,
            width, height, 0,
            this.gl.RGBA, this.gl.UNSIGNED_BYTE, null
        );
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

        // Create framebuffer
        this.maskFramebuffer = this.gl.createFramebuffer();
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.maskFramebuffer);
        this.gl.framebufferTexture2D(
            this.gl.FRAMEBUFFER,
            this.gl.COLOR_ATTACHMENT0,
            this.gl.TEXTURE_2D,
            this.maskTexture,
            0
        );

        // Unbind
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    }

    resize(width: number, height: number) {
        this.gl.viewport(0, 0, width, height);

        // Recreate framebuffer with new size
        if (this.maskTexture) {
            this.gl.deleteTexture(this.maskTexture);
        }
        if (this.maskFramebuffer) {
            this.gl.deleteFramebuffer(this.maskFramebuffer);
        }
        this.initFramebuffer(width, height);
    }

    clear(r: number, g: number, b: number, a: number) {
        this.gl.clearColor(r, g, b, a);
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }

    drawCircle(
        centerLat: number,
        centerLng: number,
        radiusKm: number,
        color: [number, number, number, number],
        zoom: number,
        bounds: {minLat: number, maxLat: number, minLng: number, maxLng: number}
    ) {
        if (!this.circleProgram) return;

        this.gl.useProgram(this.circleProgram);

        // Bind quad
        const positionLoc = this.gl.getAttribLocation(this.circleProgram, "a_position");
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadBuffer);
        this.gl.enableVertexAttribArray(positionLoc);
        this.gl.vertexAttribPointer(positionLoc, 2, this.gl.FLOAT, false, 0, 0);

        // Set uniforms
        const centerLoc = this.gl.getUniformLocation(this.circleProgram, "u_center");
        const radiusLoc = this.gl.getUniformLocation(this.circleProgram, "u_radiusKm");
        const colorLoc = this.gl.getUniformLocation(this.circleProgram, "u_color");
        const resolutionLoc = this.gl.getUniformLocation(this.circleProgram, "u_resolution");
        const zoomLoc = this.gl.getUniformLocation(this.circleProgram, "u_zoom");
        const boundsLoc = this.gl.getUniformLocation(this.circleProgram, "u_bounds");

        this.gl.uniform2f(centerLoc, centerLat, centerLng);
        this.gl.uniform1f(radiusLoc, radiusKm);
        this.gl.uniform4f(colorLoc, color[0], color[1], color[2], color[3]);
        this.gl.uniform2f(resolutionLoc, this.gl.canvas.width, this.gl.canvas.height);
        this.gl.uniform1f(zoomLoc, zoom);
        this.gl.uniform4f(boundsLoc, bounds.minLat, bounds.maxLat, bounds.minLng, bounds.maxLng);

        // Enable blending
        this.gl.enable(this.gl.BLEND);
        this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);

        // Draw
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

        this.gl.disable(this.gl.BLEND);
    }

    cleanup() {
        if (this.circleProgram) this.gl.deleteProgram(this.circleProgram);
        if (this.compositeProgram) this.gl.deleteProgram(this.compositeProgram);
        if (this.quadBuffer) this.gl.deleteBuffer(this.quadBuffer);
        if (this.maskTexture) this.gl.deleteTexture(this.maskTexture);
        if (this.maskFramebuffer) this.gl.deleteFramebuffer(this.maskFramebuffer);
    }
}

export function WebGLMaskDrawPage() {
    const mapRef = useRef<google.maps.Map | null>(null);
    const mapDivRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    const drawFuncRef = useRef<(() => void) | null>(null);

    const [shapes, setShapes] = useState<Shape[]>([]);
    const [activeTool, setActiveTool] = useState<DrawingTool>("none");
    const [selectedColor, setSelectedColor] = useState("#3B82F6");
    const [currentDrawing, setCurrentDrawing] = useState<Array<[number, number]>>([]);
    const [mousePos, setMousePos] = useState<[number, number] | null>(null);

    // Load Google Maps API
    const {isLoaded, loadError} = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY || "",
    });

    // Convert pixel coordinates to lat/lng using Google Maps projection
    const pixelToLatLng = useCallback((x: number, y: number): [number, number] | null => {
        const map = mapRef.current;
        const overlay = overlayRef.current;
        if (!map || !overlay) return null;

        const projection = overlay.getProjection();
        if (!projection) return null;

        const point = new google.maps.Point(x, y);
        const latLng = projection.fromDivPixelToLatLng(point);
        if (!latLng) return null;

        return [latLng.lng(), latLng.lat()];
    }, []);

    // Parse hex color to RGB
    const hexToRgb = useCallback((hex: string): [number, number, number] => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        return [r, g, b];
    }, []);

    // Draw function - use vertex-based approach with projection (like drawing-test)
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const map = mapRef.current;
        const overlay = overlayRef.current;

        if (!canvas || !map || !overlay) return;

        const gl = canvas.getContext("webgl", {alpha: true, premultipliedAlpha: false});
        if (!gl) return;

        const projection = (overlay as any).getProjectionSafe?.();
        if (!projection) return;

        const mapDiv = map.getDiv();
        if (!mapDiv) return;

        const dpr = window.devicePixelRatio || 1;
        const width = mapDiv.offsetWidth;
        const height = mapDiv.offsetHeight;

        if (!width || !height) return;

        // Size canvas to match map
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.width = width * dpr;
        canvas.height = height * dpr;

        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        // Create simple shader (same as drawing-test)
        const vertexShaderSource = `
            attribute vec2 a_position;
            void main() {
                gl_Position = vec4(a_position, 0.0, 1.0);
            }
        `;

        const fragmentShaderSource = `
            precision mediump float;
            uniform vec4 u_color;
            void main() {
                gl_FragColor = u_color;
            }
        `;

        const compileShader = (source: string, type: number) => {
            const shader = gl.createShader(type);
            if (!shader) return null;
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error("Shader error:", gl.getShaderInfoLog(shader));
                return null;
            }
            return shader;
        };

        const vs = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
        const fs = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);
        if (!vs || !fs) return;

        const program = gl.createProgram();
        if (!program) return;
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error("Program error:", gl.getProgramInfoLog(program));
            return;
        }

        gl.useProgram(program);

        const posLoc = gl.getAttribLocation(program, "a_position");
        const colorLoc = gl.getUniformLocation(program, "u_color");

        // Helper: convert lat/lng to NDC
        const latLngToNDC = (lng: number, lat: number): [number, number] | null => {
            const latLng = new google.maps.LatLng(lat, lng);
            const pixel = projection.fromLatLngToContainerPixel(latLng);
            if (!pixel || typeof pixel.x !== "number" || typeof pixel.y !== "number") return null;

            const normX = (pixel.x / width) * 2 - 1;
            const normY = -(pixel.y / height) * 2 + 1;
            return [normX, normY];
        };

        // Helper: calculate destination point
        const EARTH_RADIUS = 6378137;
        const MAX_MERCATOR_LAT = 85.05112878; // Web Mercator projection limit

        const destinationPoint = (lat: number, lng: number, bearing: number, distanceMeters: number): [number, number] => {
            const toRad = (deg: number) => deg * Math.PI / 180;
            const toDeg = (rad: number) => rad * 180 / Math.PI;

            const latRad = toRad(lat);
            const lngRad = toRad(lng);
            const bearingRad = toRad(bearing);

            const angularDistance = distanceMeters / EARTH_RADIUS;

            const lat2Rad = Math.asin(
                Math.sin(latRad) * Math.cos(angularDistance) +
                Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearingRad)
            );

            const lng2Rad = lngRad + Math.atan2(
                Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(latRad),
                Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(lat2Rad)
            );

            let lat2 = toDeg(lat2Rad);
            let lng2 = toDeg(lng2Rad);

            // Clamp latitude to Web Mercator bounds (prevents rendering artifacts at poles)
            if (lat2 > MAX_MERCATOR_LAT) lat2 = MAX_MERCATOR_LAT;
            if (lat2 < -MAX_MERCATOR_LAT) lat2 = -MAX_MERCATOR_LAT;

            // Normalize longitude
            while (lng2 > 180) lng2 -= 360;
            while (lng2 < -180) lng2 += 360;

            return [lng2, lat2];
        };

        // Render all circles using triangle fans
        const segments = 64;
        for (const shape of shapes) {
            if (shape.type !== "circle") continue;

            const radiusMeters = shape.radiusKm * 1000;
            const [r, g, b] = hexToRgb(shape.color);

            // Render circle (with dateline wrapping)
            const offsets = [-360, 0, 360];
            for (const offset of offsets) {
                const centerNDC = latLngToNDC(shape.center[0] + offset, shape.center[1]);
                if (!centerNDC) continue;

                const positions: number[] = [centerNDC[0], centerNDC[1]];

                for (let i = 0; i <= segments; i++) {
                    const bearing = (i / segments) * 360;
                    const [edgeLng, edgeLat] = destinationPoint(
                        shape.center[1],
                        shape.center[0],
                        bearing,
                        radiusMeters
                    );
                    const ndc = latLngToNDC(edgeLng + offset, edgeLat);
                    if (ndc) {
                        positions.push(ndc[0], ndc[1]);
                    }
                }

                if (positions.length >= 6) {
                    const buffer = gl.createBuffer();
                    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
                    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                    gl.enableVertexAttribArray(posLoc);
                    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                    gl.uniform4f(colorLoc, r, g, b, 0.5);
                    gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);
                }
            }
        }

        // Draw preview
        if (activeTool === "circle" && currentDrawing.length === 1 && mousePos) {
            const radiusKm = haversineDistance(
                currentDrawing[0][1], currentDrawing[0][0],
                mousePos[1], mousePos[0]
            );
            const radiusMeters = radiusKm * 1000;
            const [r, g, b] = hexToRgb(selectedColor);

            const centerNDC = latLngToNDC(currentDrawing[0][0], currentDrawing[0][1]);
            if (centerNDC) {
                const positions: number[] = [centerNDC[0], centerNDC[1]];

                for (let i = 0; i <= segments; i++) {
                    const bearing = (i / segments) * 360;
                    const [edgeLng, edgeLat] = destinationPoint(
                        currentDrawing[0][1],
                        currentDrawing[0][0],
                        bearing,
                        radiusMeters
                    );
                    const ndc = latLngToNDC(edgeLng, edgeLat);
                    if (ndc) {
                        positions.push(ndc[0], ndc[1]);
                    }
                }

                if (positions.length >= 6) {
                    const buffer = gl.createBuffer();
                    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
                    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                    gl.enableVertexAttribArray(posLoc);
                    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                    gl.uniform4f(colorLoc, r, g, b, 0.25);
                    gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);
                }
            }
        }
    }, [shapes, currentDrawing, mousePos, activeTool, selectedColor, hexToRgb, haversineDistance]);

    // Keep draw function ref in sync
    useEffect(() => {
        drawFuncRef.current = draw;
    }, [draw]);

    // Initialize Google Maps and canvas overlay
    useEffect(() => {
        if (!isLoaded || !mapDivRef.current) return;

        // Create Google Maps instance
        const map = new google.maps.Map(mapDivRef.current, {
            center: {lat: 20, lng: 0},
            zoom: 2,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            clickableIcons: false,
            gestureHandling: "greedy",
        });

        mapRef.current = map;

        // Create canvas overlay (like drawing-test does)
        const canvas = document.createElement("canvas");
        canvas.style.position = "absolute";
        canvas.style.left = "0";
        canvas.style.top = "0";
        canvas.style.pointerEvents = "none";

        const mapDiv = map.getDiv();
        mapDiv.appendChild(canvas);

        // Store canvas reference
        canvasRef.current = canvas;

        // Create OverlayView for projection
        const overlay = createWebGLOverlay(drawFuncRef);
        overlay.setMap(map);
        overlayRef.current = overlay;

        // Initial draw
        setTimeout(() => {
            if (drawFuncRef.current) {
                drawFuncRef.current();
            }
        }, 100);

        return () => {
            overlay.setMap(null);
            if (canvasRef.current) {
                canvasRef.current.remove();
            }
            overlayRef.current = null;
            mapRef.current = null;
        };
    }, [isLoaded]);

    // Redraw when shapes change
    useEffect(() => {
        if (overlayRef.current && mapRef.current) {
            draw();
        }
    }, [shapes, currentDrawing, draw]);

    // Mouse handlers for map clicks
    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!e.latLng || activeTool === "none") return;

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        if (activeTool === "circle") {
            if (currentDrawing.length === 0) {
                setCurrentDrawing([[lng, lat]]);
            } else {
                const radiusKm = haversineDistance(
                    currentDrawing[0][1], currentDrawing[0][0],
                    lat, lng
                );
                setShapes(prev => [...prev, {
                    id: Math.random().toString(36).substr(2, 9),
                    type: "circle",
                    center: currentDrawing[0],
                    radiusKm,
                    color: selectedColor
                }]);
                setCurrentDrawing([]);
                setActiveTool("none");
            }
        } else {
            setCurrentDrawing(prev => [...prev, [lng, lat]]);
        }
    }, [activeTool, currentDrawing, selectedColor]);

    const handleMapMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        setMousePos([e.latLng.lng(), e.latLng.lat()]);
    }, []);

    // Attach map event listeners
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const clickListener = map.addListener("click", handleMapClick);
        const mouseMoveListener = map.addListener("mousemove", handleMapMouseMove);

        return () => {
            google.maps.event.removeListener(clickListener);
            google.maps.event.removeListener(mouseMoveListener);
        };
    }, [handleMapClick, handleMapMouseMove]);

    // Toggle map dragging based on active tool
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        if (activeTool !== "none") {
            // Disable map dragging when drawing
            map.setOptions({
                draggable: false,
                zoomControl: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
            });
        } else {
            // Re-enable map interactions
            map.setOptions({
                draggable: true,
                zoomControl: true,
                scrollwheel: true,
                disableDoubleClickZoom: false,
            });
        }
    }, [activeTool]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === "Enter" && (activeTool === "polygon" || activeTool === "line")) {
            if (currentDrawing.length >= 2) {
                setShapes(prev => [...prev, {
                    id: Math.random().toString(36).substr(2, 9),
                    type: activeTool,
                    vertices: currentDrawing,
                    color: selectedColor
                }]);
                setCurrentDrawing([]);
                setActiveTool("none");
            }
        } else if (e.key === "Escape") {
            setCurrentDrawing([]);
            setActiveTool("none");
        }
    }, [activeTool, currentDrawing, selectedColor]);

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const startDrawing = (tool: DrawingTool) => {
        setActiveTool(tool);
        setCurrentDrawing([]);
    };

    const deleteShape = (id: string) => {
        setShapes(prev => prev.filter(s => s.id !== id));
    };

    const clearAll = () => {
        setShapes([]);
        setCurrentDrawing([]);
        setActiveTool("none");
    };

    if (loadError) {
        return <div className="h-screen flex items-center justify-center">
            <div className="text-red-600">Error loading Google Maps: {loadError.message}</div>
        </div>;
    }

    if (!isLoaded) {
        return <div className="h-screen flex items-center justify-center">
            <div className="text-gray-600">Loading Google Maps...</div>
        </div>;
    }

    return (
        <div className="h-screen flex flex-col pt-36">
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <h1 className="text-2xl font-semibold text-gray-800">WebGL + Google Maps Drawing</h1>
                <p className="text-sm text-gray-600 mt-1">GPU-accelerated geofence visualization overlaid on Google Maps</p>
            </div>

            <div className="flex-1 flex overflow-hidden">
                <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
                    <div className="p-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-800 mb-3">Drawing Tools</h2>
                        <div className="space-y-2">
                            <button
                                onClick={() => startDrawing("circle")}
                                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                                    activeTool === "circle"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                Circle (WebGL)
                            </button>
                            <button
                                onClick={() => startDrawing("polygon")}
                                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                                    activeTool === "polygon"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                disabled
                            >
                                Polygon (Coming Soon)
                            </button>
                            <button
                                onClick={() => startDrawing("line")}
                                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                                    activeTool === "line"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                disabled
                            >
                                Line (Coming Soon)
                            </button>
                        </div>
                    </div>

                    <div className="p-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-800 mb-3">Color</h2>
                        <input
                            type="color"
                            value={selectedColor}
                            onChange={(e) => setSelectedColor(e.target.value)}
                            className="w-full h-10 rounded cursor-pointer"
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-semibold text-gray-800">Shapes ({shapes.length})</h2>
                            {shapes.length > 0 && (
                                <button
                                    onClick={clearAll}
                                    className="text-sm text-red-600 hover:text-red-700"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {shapes.map((shape) => (
                                <div
                                    key={shape.id}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                >
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="w-4 h-4 rounded"
                                            style={{backgroundColor: shape.color}}
                                        />
                                        <span className="text-sm capitalize">{shape.type}</span>
                                    </div>
                                    <button
                                        onClick={() => deleteShape(shape.id)}
                                        className="text-red-600 hover:text-red-700"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 border-t border-gray-100">
                        <h2 className="font-semibold text-gray-800 mb-3">Test Circles</h2>
                        <div className="space-y-2">
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [0, 80],
                                        radiusKm: 1200,
                                        color: "#3B82F6"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                North Pole (80°, 1200km)
                            </button>
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [0, -80],
                                        radiusKm: 1200,
                                        color: "#EF4444"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                South Pole (-80°, 1200km)
                            </button>
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [170, 0],
                                        radiusKm: 2000,
                                        color: "#10B981"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                Date Line (170°, 2000km)
                            </button>
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [0, 0],
                                        radiusKm: 5000,
                                        color: "#F59E0B"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                Equator Large (0°, 5000km)
                            </button>
                        </div>
                    </div>

                    <div className="p-4 border-t border-gray-100 text-xs text-gray-500">
                        <p className="font-semibold mb-2">Features:</p>
                        <ul className="space-y-1">
                            <li>• WebGL overlay on Google Maps</li>
                            <li>• Geodesic distance calculations</li>
                            <li>• GPU-accelerated rendering</li>
                            <li>• Pole & dateline wrapping</li>
                            <li>• Click map to draw circles</li>
                        </ul>
                    </div>
                </div>

                <div className="flex-1 relative">
                    {/* Google Maps container */}
                    <div
                        ref={mapDivRef}
                        className="w-full h-full"
                        style={{cursor: activeTool !== "none" ? "crosshair" : "default"}}
                    />
                </div>
            </div>
        </div>
    );
}
